import { ApiError, ApiNetworkError, messageForApiError } from '@repo/api-client';

/**
 * One place where a caught `unknown` becomes a sentence a person reads.
 *
 * `messageForApiError` already answers for a refusal, and it is the only thing
 * that should — the API merges several failures under one code deliberately and
 * a screen that re-separates them re-opens what the server closed. What it does
 * not answer for is the other two ways a call ends: nothing came back at all,
 * and something went wrong that was neither. `ChangeCredential` wrote those two
 * sentences as local constants, which was right for one screen and is wrong for
 * seven — the back office would have seven wordings of "the network is down".
 *
 * This lives in `apps/web/src/api` rather than in `packages/api-client` for the
 * same reason the session teardown does: the terminals consume that package and
 * answer both of these differently. A till states offline plainly and keeps
 * working; the back office cannot, and says so.
 */

/**
 * Not a refusal — nothing answered. Worded as a connection to check rather than
 * as a fault, and it says explicitly that nothing changed, because the common
 * case is somebody deciding whether it is safe to press the button again.
 */
export const UNREACHABLE =
  'Could not reach the server. Check the network connection and try again — nothing was changed.';

/** Neither a refusal nor the network. It says so rather than blaming the API. */
export const UNEXPECTED =
  'Something went wrong that this screen could not identify. Try again, and report it if it keeps happening.';

/**
 * What to tell somebody about a failed call.
 *
 * Takes `unknown` because that is what a `catch` binds. The three branches are
 * exhaustive by construction: `packages/api-client` throws exactly two classes,
 * and everything else is a bug in this app rather than an answer from the API.
 */
export function messageForFailure(error: unknown): string {
  if (error instanceof ApiError) return messageForApiError(error);

  if (error instanceof ApiNetworkError) return UNREACHABLE;

  return UNEXPECTED;
}

/**
 * Whether a failure is worth offering a retry button for.
 *
 * The network is always worth retrying — that is the whole shape of a park on a
 * hill with intermittent connectivity. A refusal is retryable only when the
 * API's own registry says so, which is read off the error rather than guessed:
 * every `AUTH_*` code is non-retryable, and a "try again" under one of those is
 * a button that cannot work and that staff will press until they give up.
 */
export function isWorthRetrying(error: unknown): boolean {
  if (error instanceof ApiNetworkError) return true;

  return error instanceof ApiError && error.retryable;
}

/**
 * Whether this failure was the network rather than an answer.
 *
 * Kept separate from the message because offline is an operating mode and not
 * an error — `docs/ui-conventions.md` is explicit that the wording must not
 * alarm somebody mid-queue, and a screen that wants to say so needs to be able
 * to ask.
 */
export function isOffline(error: unknown): boolean {
  return error instanceof ApiNetworkError;
}

/**
 * Whether the API refused the caller rather than failing.
 *
 * Kept here beside `isOffline` for the same reason: a screen that wants to
 * answer a refusal differently from a fault has to be able to ask, and asking by
 * hand means writing `error instanceof ApiError && error.code === …` at every
 * site — which is where somebody eventually reaches for `error.status === 403`
 * instead and catches a lease expiry with it.
 *
 * `AUTH_FORBIDDEN` is the whole of it. `AUTH_NO_OUTLET_ASSIGNMENT` is also a
 * 403 and is deliberately **not** included: it means the caller may do this but
 * is not posted anywhere it applies, which is a different sentence and a
 * different remedy.
 */
export function isForbidden(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'AUTH_FORBIDDEN';
}

/**
 * Whether the API said there is no such thing.
 *
 * Worth a predicate because on some routes this is an *answer* rather than a
 * failure — the price-at-an-instant lookup answers 404 for a moment before the
 * item had any price, and that is the honest reply to the question, not a
 * failure to reply. A screen in that position must be able to tell the two
 * apart before it words anything.
 */
export function isNotFound(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'RESOURCE_NOT_FOUND';
}
